import { useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import api from '../../api/client'
import { useAuthStore } from '../../stores/authStore'
import { isProOrTrial } from '../../utils/plan'
import { useLocale } from '../../i18n/context'
import AiRateLimitedState from './AiRateLimitedState'

// ── Types ─────────────────────────────────────────────────────────────────────

type TabId = 'tatoeba' | 'reference' | 'ai'
type FetchStatus = 'idle' | 'loading' | 'success' | 'error' | 'rate-limited'

interface TatoebaPair {
  target_sentence: string
  source_sentence: string
  source_language: string
  target_language: string
}

interface WordReferenceResult {
  ipa: string | null
  pos: string | null
  forms: { label: string; value: string }[]
  synonyms: string[]
  antonyms: string[]
  // Inflection label for the queried term (e.g. "noun, plural") or null when
  // the term is its own lemma / the form cannot be confidently classified.
  form?: string | null
}

interface TabState<T> {
  status: FetchStatus
  data: T | null
  error: string
  rateLimitInfo?: { limit: number; used: number }
  rateLimitMessage?: string
}

function freshTab<T>(): TabState<T> {
  return { status: 'idle', data: null, error: '' }
}

// ── YouGlish language name map ────────────────────────────────────────────────

const YOUGLISH_LANG: Record<string, string> = {
  en: 'english', es: 'spanish', fr: 'french', de: 'german',
  it: 'italian', pt: 'portuguese', ru: 'russian',
  zh: 'chinese', ja: 'japanese', ko: 'korean',
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface WordReferencePanelProps {
  term: string
  isOpen: boolean
  onClose: () => void
  targetLanguage: string
  sourceLanguage: string
  /** Optional example sentence passed to AI explain for richer context */
  sentence?: string
  /**
   * 'modal' (default): fixed overlay with backdrop — used in learn mode.
   * 'column': inline block rendered inside a right-column container — used in deck view.
   */
  variant?: 'modal' | 'column'
  /**
   * Called when the user clicks a synonym/antonym chip to switch panel context.
   * Provided only in deck/column view. In learn mode (modal) chips are display-only.
   */
  onSwitchTerm?: (word: string) => void
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export default function WordReferencePanel({
  term,
  isOpen,
  onClose,
  targetLanguage,
  sourceLanguage,
  sentence,
  variant = 'modal',
  onSwitchTerm,
}: WordReferencePanelProps) {
  const { tr } = useLocale()
  const shouldReduceMotion = useReducedMotion()
  const [activeTab, setActiveTab] = useState<TabId>('tatoeba')
  const [tatoeba, setTatoeba] = useState<TabState<TatoebaPair[]>>(freshTab())
  const [aiState, setAiState] = useState<TabState<string>>(freshTab())
  const [isTransitioning, setIsTransitioning] = useState(false)
  const prevTermRef = useRef<string>('')

  const panelRef = useRef<HTMLDivElement>(null)

  const fetchedForRef = useRef<Record<'tatoeba' | 'ai', string | null>>({
    tatoeba: null, ai: null,
  })
  const abortRefs = useRef<Record<'tatoeba' | 'ai', AbortController | null>>({
    tatoeba: null, ai: null,
  })
  const aiFetchedFor = useRef<string | null>(null)

  // Reset manual-fetch tab data when term changes; TanStack Query handles reference cache
  useEffect(() => {
    setTatoeba(freshTab())
    setAiState(freshTab())
    fetchedForRef.current = { tatoeba: null, ai: null }
    aiFetchedFor.current = null
    Object.values(abortRefs.current).forEach((c) => c?.abort())
  }, [term])

  // Cross-fade inner content when term switches (not on first open)
  useEffect(() => {
    if (!term || !prevTermRef.current || prevTermRef.current === term || shouldReduceMotion) {
      prevTermRef.current = term
      return
    }
    prevTermRef.current = term
    setIsTransitioning(true)
    const t = setTimeout(() => setIsTransitioning(false), 120)
    return () => clearTimeout(t)
  }, [term, shouldReduceMotion])

  // Reference data via TanStack Query — cached by (term, targetLanguage), no re-fetch on tab switch
  const {
    data: referenceData,
    isLoading: refLoading,
    isError: refError,
  } = useQuery<WordReferenceResult>({
    queryKey: ['word-reference', term, targetLanguage],
    queryFn: async () => {
      const res = await api.get<WordReferenceResult>(
        `/api/v1/words/${encodeURIComponent(term)}/reference/`,
        { params: { target_language: targetLanguage } },
      )
      return res.data
    },
    enabled: isOpen && !!term,
    staleTime: 30 * 60 * 1000,   // 30 min client-side; backend caches 30 days
    gcTime: 60 * 60 * 1000,
  })

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [isOpen, onClose])

  // Column mode: close on click-outside, but NOT when clicking a card trigger button
  useEffect(() => {
    if (variant !== 'column' || !isOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Element
      if (panelRef.current?.contains(target)) return
      if (target.closest('[data-ref-trigger]')) return
      onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [variant, isOpen, onClose])

  // Tatoeba: lazy fetch on tab activation
  useEffect(() => {
    if (!isOpen || !term) return
    if (activeTab === 'tatoeba') fetchTatoeba()
    // reference handled by useQuery; ai has its own effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, term])

  const fetchTatoeba = useCallback(() => {
    if (fetchedForRef.current.tatoeba === term) return
    abortRefs.current.tatoeba?.abort()
    const ctrl = new AbortController()
    abortRefs.current.tatoeba = ctrl
    setTatoeba({ status: 'loading', data: null, error: '' })
    api
      .get<{ results: TatoebaPair[] }>(
        `/api/v1/words/${encodeURIComponent(term)}/tatoeba/`,
        { params: { source_language: sourceLanguage, target_language: targetLanguage }, signal: ctrl.signal },
      )
      .then((res) => {
        if (!ctrl.signal.aborted) {
          fetchedForRef.current.tatoeba = term
          setTatoeba({ status: 'success', data: res.data.results, error: '' })
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        setTatoeba({ status: 'error', data: null, error: tr.wordReference.examplesLoadError })
      })
  }, [term, sourceLanguage, targetLanguage, tr])

  const user = useAuthStore((s) => s.user)
  const [aiRetry, setAiRetry] = useState(0)

  useEffect(() => {
    if (!isOpen || activeTab !== 'ai' || !term) {
      abortRefs.current.ai?.abort()
      return
    }
    if (aiFetchedFor.current === term) return

    aiFetchedFor.current = null
    abortRefs.current.ai?.abort()
    const ctrl = new AbortController()
    abortRefs.current.ai = ctrl

    setAiState({ status: 'loading', data: null, error: '' })

    api
      .post<{ explanation: string }>(
        '/api/v1/ai/explain/',
        { word: term, sentence: sentence ?? null },
        { signal: ctrl.signal },
      )
      .then((res) => {
        aiFetchedFor.current = term
        setAiState({ status: 'success', data: res.data.explanation, error: '' })
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err)) return
        type RateLimitDetail = { detail?: string; limit?: number; used?: number }
        const httpStatus = (err as { response?: { status?: number } })?.response?.status
        if (httpStatus === 429) {
          const detail = (err as { response?: { data?: { detail?: RateLimitDetail } } })
            ?.response?.data?.detail
          setAiState({
            status: 'rate-limited',
            data: null,
            error: '',
            rateLimitInfo: detail?.limit != null
              ? { limit: detail.limit, used: detail.used ?? detail.limit }
              : undefined,
            rateLimitMessage: typeof detail?.detail === 'string' ? detail.detail : undefined,
          })
        } else {
          setAiState({ status: 'error', data: null, error: tr.wordReference.aiLoadError })
        }
      })

    return () => { ctrl.abort() }
  }, [isOpen, activeTab, term, sentence, aiRetry, tr])

  const retryAi = useCallback(() => {
    aiFetchedFor.current = null
    setAiRetry((n) => n + 1)
  }, [])

  const tabs: { id: TabId; label: string }[] = [
    { id: 'tatoeba', label: tr.wordReference.tabExamples },
    { id: 'reference', label: tr.wordReference.tabReference },
    { id: 'ai', label: tr.wordReference.tabAi },
  ]

  const youglishLang = YOUGLISH_LANG[targetLanguage] ?? 'english'
  const youglishUrl = `https://youglish.com/pronounce/${encodeURIComponent(term)}/${youglishLang}`

  // ── Shared inner content ──────────────────────────────────────────────────

  const panelHeader = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0,
      gap: 8,
    }}>
      <span style={{
        fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-content)',
        color: 'var(--text-primary)', flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 0.12s ease',
      }}>
        {term || '\u00A0'}
      </span>
      {/* YouGlish quick-action — always visible, points to the current word */}
      {term && (
        <a
          href={youglishUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tr.wordReference.youglishAriaLabel(term)}
          title={tr.wordReference.youglishTitle}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 'var(--radius-pill)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            fontSize: 12, fontWeight: 500,
            textDecoration: 'none', whiteSpace: 'nowrap',
            transition: 'color 0.15s, border-color 0.15s',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLAnchorElement
            el.style.color = 'var(--text-primary)'
            el.style.borderColor = 'var(--text-secondary)'
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLAnchorElement
            el.style.color = 'var(--text-secondary)'
            el.style.borderColor = 'var(--border)'
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          {'YouGlish' /* i18n-exempt: brand name */}
        </a>
      )}
      <button
        onClick={onClose}
        aria-label={tr.wordReference.closeAria}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', padding: 8, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 36, minHeight: 36, flexShrink: 0,
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)' }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )

  const panelTabs = (
    <div style={{
      display: 'flex', borderBottom: '1px solid var(--border)',
      padding: '0 16px', flexShrink: 0, gap: 4,
    }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => setActiveTab(t.id)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 12px', fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
            color: activeTab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
            borderBottom: activeTab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1, borderRadius: 0, minHeight: 40,
            transition: 'color 0.15s',
            whiteSpace: 'nowrap',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )

  const panelBody = (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', opacity: isTransitioning ? 0 : 1, transition: 'opacity 0.12s ease' }}>
      {activeTab === 'tatoeba' && (
        <TatoebaTab state={tatoeba} onRetry={fetchTatoeba} term={term} />
      )}
      {activeTab === 'reference' && (
        <ReferenceTab
          data={referenceData}
          isLoading={refLoading}
          isError={refError}
          onSwitchTerm={onSwitchTerm}
        />
      )}
      {activeTab === 'ai' && (
        <AiTab state={aiState} onRetry={retryAi} isPro={isProOrTrial(user)} />
      )}
    </div>
  )

  // ── Column variant ────────────────────────────────────────────────────────

  if (variant === 'column') {
    return (
      <div
        ref={panelRef}
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-float)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        role="complementary"
        aria-label={tr.wordReference.panelAria}
      >
        {panelHeader}
        {panelTabs}
        {panelBody}
      </div>
    )
  }

  // ── Modal variant (default; used in learn mode) ───────────────────────────

  return (
    <>
      <div
        className={`word-ref-backdrop${isOpen ? ' word-ref-backdrop--open' : ''}`}
        onClick={onClose}
      />
      <div
        className={`word-ref-panel${isOpen ? ' word-ref-panel--open' : ''}`}
        role="complementary"
        aria-label={tr.wordReference.panelAria}
      >
        {panelHeader}
        {panelTabs}
        {panelBody}
      </div>
    </>
  )
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
      <svg
        width="22" height="22" viewBox="0 0 24 24"
        fill="none" stroke="var(--accent)" strokeWidth="2.5"
        style={{ animation: 'spin 0.7s linear infinite' }}
      >
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <circle cx="12" cy="12" r="10" strokeOpacity="0.2" />
        <path d="M12 2a10 10 0 0 1 10 10" />
      </svg>
    </div>
  )
}

function InlineError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { tr } = useLocale()
  return (
    <div style={{
      padding: '20px 0', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 10, textAlign: 'center',
    }}>
      <span style={{ fontSize: 13, color: 'var(--danger)' }}>{message}</span>
      <button
        onClick={onRetry}
        style={{
          padding: '7px 18px', borderRadius: 50, border: '1px solid var(--border)',
          background: 'transparent', color: 'var(--text-primary)', fontSize: 13,
          cursor: 'pointer', minHeight: 36,
        }}
      >
        {tr.wordReference.tryAgain}
      </button>
    </div>
  )
}

// ── Tatoeba tab ───────────────────────────────────────────────────────────────

function highlightLemma(text: string, lemma: string): React.ReactNode {
  if (!lemma) return text
  const escaped = lemma.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return parts.map((part, i) =>
    part.toLowerCase() === lemma.toLowerCase()
      ? <mark key={i} style={{ background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 2, padding: '0 1px', fontWeight: 600 }}>{part}</mark>
      : part
  )
}

function TatoebaTab({ state, onRetry, term }: { state: TabState<TatoebaPair[]>; onRetry: () => void; term: string }) {
  const { tr } = useLocale()
  if (state.status === 'loading' || state.status === 'idle') return <Spinner />
  if (state.status === 'error') return <InlineError message={state.error} onRetry={onRetry} />
  if (!state.data?.length) {
    return (
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, margin: 0, paddingTop: 8 }}>
        {tr.wordReference.noExamplesFound(term)}
      </p>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {state.data.map((pair, i) => (
        <div key={i} style={{
          paddingBottom: 16,
          borderBottom: i < state.data!.length - 1 ? '1px solid var(--border)' : 'none',
        }}>
          <p style={{
            margin: '0 0 6px', fontSize: 16, fontFamily: 'var(--font-content)',
            color: 'var(--text-primary)', lineHeight: 1.55,
          }}>
            {highlightLemma(pair.target_sentence, term)}
          </p>
          <p style={{
            margin: 0, fontSize: 13, fontFamily: 'var(--font-content)',
            color: 'var(--text-secondary)', lineHeight: 1.5, fontStyle: 'italic',
          }}>
            {pair.source_sentence}
          </p>
        </div>
      ))}
    </div>
  )
}

// ── Reference tab ─────────────────────────────────────────────────────────────

function ReferenceTabSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
      <style>{`@keyframes ref-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      <div style={{ height: 32, width: 110, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', animation: 'ref-pulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 13, width: 70, background: 'var(--bg-secondary)', borderRadius: 4, animation: 'ref-pulse 1.5s ease-in-out infinite' }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[72, 58, 84, 65, 78].map((w, i) => (
          <div key={i} style={{ height: 28, width: w, background: 'var(--bg-secondary)', borderRadius: 50, animation: 'ref-pulse 1.5s ease-in-out infinite' }} />
        ))}
      </div>
    </div>
  )
}

function WordChip({
  word,
  onSwitch,
  variant: chipVariant = 'synonym',
}: {
  word: string
  onSwitch?: (w: string) => void
  variant?: 'synonym' | 'antonym'
}) {
  const clickable = !!onSwitch
  const isAntonym = chipVariant === 'antonym'
  return (
    <button
      onClick={clickable ? () => onSwitch!(word) : undefined}
      style={{
        padding: '4px 12px',
        background: isAntonym ? 'transparent' : 'var(--accent-soft)',
        border: `1px solid ${isAntonym ? 'var(--border)' : 'transparent'}`,
        borderRadius: 'var(--radius-pill)',
        fontSize: 13,
        fontFamily: 'var(--font-content)',
        color: isAntonym ? 'var(--text-secondary)' : 'var(--accent)',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 0.15s, color 0.15s, border-color 0.15s',
        minHeight: 28,
      }}
      onMouseEnter={clickable ? (e) => {
        const el = e.currentTarget as HTMLButtonElement
        el.style.background = 'var(--accent-soft)'
        el.style.color = 'var(--accent)'
        el.style.borderColor = 'transparent'
      } : undefined}
      onMouseLeave={clickable ? (e) => {
        const el = e.currentTarget as HTMLButtonElement
        el.style.background = isAntonym ? 'transparent' : 'var(--accent-soft)'
        el.style.color = isAntonym ? 'var(--text-secondary)' : 'var(--accent)'
        el.style.borderColor = isAntonym ? 'var(--border)' : 'transparent'
      } : undefined}
    >
      {word}
    </button>
  )
}

function ReferenceTab({
  data,
  isLoading,
  isError,
  onSwitchTerm,
}: {
  data: WordReferenceResult | undefined
  isLoading: boolean
  isError: boolean
  onSwitchTerm?: (word: string) => void
}) {
  const { tr } = useLocale()
  if (isLoading) return <ReferenceTabSkeleton />
  if (isError) {
    return (
      <p style={{ color: 'var(--danger)', fontSize: 13, margin: 0, paddingTop: 8 }}>
        {tr.wordReference.referenceLoadError}
      </p>
    )
  }

  const hasData = data && (
    data.ipa || data.pos || data.form ||
    (data.forms?.length ?? 0) > 0 ||
    (data.synonyms?.length ?? 0) > 0 ||
    (data.antonyms?.length ?? 0) > 0
  )

  if (!hasData) {
    return (
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, margin: 0, paddingTop: 8 }}>
        {tr.wordReference.noReferenceData}
      </p>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* IPA — monospace pill at the top */}
      {data!.ipa && (
        <div>
          <span style={{
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            fontSize: 17, color: 'var(--text-primary)',
            background: 'var(--bg-secondary)',
            padding: '5px 12px',
            borderRadius: 'var(--radius-sm)',
            display: 'inline-block',
            letterSpacing: '0.02em',
          }}>
            {data!.ipa}
          </span>
        </div>
      )}

      {/* Part of speech (with inflection label when the term is an inflected form) */}
      {(data!.pos || data!.form) && (
        <p style={{
          margin: 0, fontSize: 11, color: 'var(--text-secondary)',
          letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
        }}>
          {data!.form ? tr.learn.formLabel(data!.form) : data!.pos}
        </p>
      )}

      {/* Irregular forms — only rendered when present */}
      {(data!.forms?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{
            margin: 0, fontSize: 11, color: 'var(--text-secondary)',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
          }}>
            {tr.wordReference.irregularForms}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data!.forms.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <span style={{
                  fontSize: 11, color: 'var(--text-secondary)',
                  minWidth: 90, textTransform: 'capitalize',
                }}>
                  {f.label}
                </span>
                <span style={{
                  fontSize: 14, color: 'var(--text-primary)',
                  fontFamily: 'var(--font-content)',
                }}>
                  {f.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Synonyms */}
      {(data!.synonyms?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{
            margin: 0, fontSize: 11, color: 'var(--text-secondary)',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
          }}>
            {tr.wordReference.synonyms}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data!.synonyms.map((word) => (
              <WordChip key={word} word={word} onSwitch={onSwitchTerm} variant="synonym" />
            ))}
          </div>
        </div>
      )}

      {/* Antonyms */}
      {(data!.antonyms?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{
            margin: 0, fontSize: 11, color: 'var(--text-secondary)',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
          }}>
            {tr.wordReference.antonyms}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data!.antonyms.map((word) => (
              <WordChip key={word} word={word} onSwitch={onSwitchTerm} variant="antonym" />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── AI tab ────────────────────────────────────────────────────────────────────

function AiTab({ state, onRetry, isPro }: { state: TabState<string>; onRetry: () => void; isPro?: boolean }) {
  if (state.status === 'loading' || state.status === 'idle') return <Spinner />
  if (state.status === 'rate-limited') {
    return (
      <AiRateLimitedState
        isPro={!!isPro}
        rateLimitInfo={state.rateLimitInfo}
        backendMessage={state.rateLimitMessage}
        compact
      />
    )
  }
  if (state.status === 'error') return <InlineError message={state.error} onRetry={onRetry} />
  if (!state.data) return null
  const paragraphs = state.data.split(/\n\n+/)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {paragraphs.map((para, i) => (
        <p key={i} style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--text-primary)' }}>
          {para.split('\n').map((line, j, arr) =>
            j < arr.length - 1
              ? <span key={j}>{line}<br /></span>
              : <span key={j}>{line}</span>
          )}
        </p>
      ))}
    </div>
  )
}
